import { computed, observable, when, makeObservable, actionBound } from "mobx";
import ShippingAddressDTO, { ShippingAddressType } from "@/dto/ShippingAddressDTO";
import WaybillTemplateDTO from "@/dto/template/WaybillTemplateDTO";
import { getList as getWaybillTemplates } from "@/services/api/setting/waybillTemplate";
import { getList as getShippingAddress } from "@/services/api/setting/shippingAddress";
import {
    getAllMaxQuantityPerPackageSetProducts,
    ProductMaxQuantityPerPackageDto,
} from "@/services/api/setting/product";

/**
 * 打印相关缓存store
 *
 * 减少请求
 * 1.进入店铺重新刷新获取新的列表
 * 2.进入列表主动属性
 * 3.主动刷新
 */
export default class WaybillPrintStore {
    constructor() {
        makeObservable(this, {
            waybillTemplatesLoading: observable,
            waybillTemplates: observable,
            shippingAddressesLoading: observable,
            shippingAddresses: observable,
            allShippingAddresses: observable,
            shippingAddressMapping: observable,
            ensure: actionBound,
            ensureWaybillTemplates: actionBound,
            ensureShippingAddresses: actionBound,
            clear: actionBound,
            loadWaybillTemplate: actionBound,
            loadShippingAddresses: actionBound,
            ensureMaxQuantityPerPackageSetProducts: actionBound,
            loadingAllMaxQuantityPerPackageSetProducts: observable,
            productMaxQuantityPerPackages: computed,
            productMaxQuantityPerPackagesShopIdMapping: observable,
            loadAllMaxQuantityPerPackageSetProducts: actionBound,
            loadAllMaxQuantityPerPackageSetProductsByOneShop: actionBound,
        });
    }

    waybillTemplatesLoading = false;

    waybillTemplates: WaybillTemplateDTO[] = [];

    shippingAddressesLoading = false;

    /** 已过滤（已删除和散客）的发货人 */
    shippingAddresses: ShippingAddressDTO[] = [];

    /** 完整后端返回的发货人列表 */
    allShippingAddresses: ShippingAddressDTO[] = [];

    /** 保留已删除和散客类型的发货地址（订单列表展示使用） */
    shippingAddressMapping = new Map<number, ShippingAddressDTO>();

    /**
     * 保证有模板和发货人数据至少存在,但不能保证是最新的数据
     */
    async ensure() {
        await this.ensureWaybillTemplates();

        await this.ensureShippingAddresses();
    }

    async ensureWaybillTemplates() {
        await when(() => !this.waybillTemplatesLoading);

        if (!this.waybillTemplates.length) {
            await this.loadWaybillTemplate();
        }

        return this.waybillTemplates;
    }

    async ensureShippingAddresses() {
        if (!this.shippingAddresses.length) {
            await this.loadShippingAddresses();
        }
    }

    clear() {
        this.waybillTemplates = [];
        this.shippingAddresses = [];
        this.shippingAddressMapping = new Map();
        this.productMaxQuantityPerPackagesShopIdMapping.clear();
    }

    async loadWaybillTemplate() {
        if (this.waybillTemplatesLoading) {
            return;
        }
        this.waybillTemplatesLoading = true;
        try {
            const waybillTemplates = await getWaybillTemplates();

            this.waybillTemplates = waybillTemplates;
        } catch (error) {
            console.error(error);
        } finally {
            this.waybillTemplatesLoading = false;
        }
    }

    async loadShippingAddresses() {
        if (this.shippingAddressesLoading) {
            await when(() => !this.shippingAddressesLoading);

            return;
        }
        this.shippingAddressesLoading = true;
        try {
            const allShippingAddresses = await getShippingAddress(false, false);

            this.allShippingAddresses = allShippingAddresses;
            this.shippingAddresses = allShippingAddresses.filter(
                ({ deleted, type }) => !deleted && type !== ShippingAddressType.CASUAL,
            );

            const map = new Map<number, ShippingAddressDTO>();

            allShippingAddresses?.forEach((shippingAddress) => {
                map.set(shippingAddress.id, shippingAddress);
            });

            this.shippingAddressMapping = map;
        } catch (error) {
            console.error(error);
        } finally {
            this.shippingAddressesLoading = false;
        }
    }

    async ensureMaxQuantityPerPackageSetProducts(shopIds: number[]) {
        const needReloadShopIds: number[] = [];

        for (let i = 0; i < shopIds?.length; i++) {
            const shopId = shopIds[i];

            const caches = this.productMaxQuantityPerPackagesShopIdMapping.get(shopId);

            if (!caches?.length) {
                needReloadShopIds.push(shopId);
            }
        }

        if (needReloadShopIds.length) {
            await this.loadAllMaxQuantityPerPackageSetProducts(needReloadShopIds);
        }
    }

    loadingAllMaxQuantityPerPackageSetProducts = false;

    get productMaxQuantityPerPackages() {
        const list: ProductMaxQuantityPerPackageDto[] = [];

        this.productMaxQuantityPerPackagesShopIdMapping.forEach((packages) => {
            list.push(...packages);
        });

        return list;
    }

    /**
     * shopId -> ProductMaxQuantityPerPackageDto[]
     */
    productMaxQuantityPerPackagesShopIdMapping = new Map<number, ProductMaxQuantityPerPackageDto[]>();

    async loadAllMaxQuantityPerPackageSetProducts(shopIds: number[]) {
        this.loadingAllMaxQuantityPerPackageSetProducts = true;

        try {
            for (let i = 0; i < shopIds.length; i++) {
                const shopId = shopIds[i];

                await this.loadAllMaxQuantityPerPackageSetProductsByOneShop(shopId);
            }
        } finally {
            this.loadingAllMaxQuantityPerPackageSetProducts = false;
        }
    }

    async loadAllMaxQuantityPerPackageSetProductsByOneShop(shopId: number) {
        const packages = await getAllMaxQuantityPerPackageSetProducts(shopId);

        this.productMaxQuantityPerPackagesShopIdMapping.set(shopId, packages);
    }
}
